let WS_URL = import.meta.env.VITE_WS_URL;

class WebSocketClient {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = 5000;
    this.connecting = false;
    this.heartbeatIntervals = new Map();
    this.lastToken = null;
  }

  connect(token = this.lastToken || localStorage.getItem("token")) {
    if (this.connecting) {
      return;
    }

    if (!token) {
      return;
    }

    try {
      this.connecting = true;
      this.lastToken = token;
      if (this.ws) {
        this.ws.close();
      }

      const ws = new WebSocket(WS_URL);
      this.ws = ws;

      // Handlers of a socket that was replaced or cleaned up meanwhile must not
      // touch the current one (it may be null or still connecting).
      ws.onopen = () => {
        if (ws !== this.ws) return;
        ws.send(JSON.stringify({ type: "auth", token }));
        this.connecting = false;
        this.reconnectAttempts = 0;
        this.startPing();
        window.dispatchEvent(new CustomEvent("ws_open"));
      };

      ws.onclose = (_event) => {
        if (ws !== this.ws) return;
        this.connecting = false;
        this.cleanup({ keepToken: true });
        this.handleReconnect();
      };

      ws.onerror = (_error) => {
        if (ws !== this.ws) return;
        this.connecting = false;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "pong") return;

          const customEvent = new CustomEvent(data.type, {
            detail: data,
          });
          window.dispatchEvent(customEvent);
        } catch (_) {
          // ignore non-JSON websocket messages
        }
      };
    } catch {
      this.connecting = false;
      this.handleReconnect();
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => this.connect(this.lastToken), this.reconnectTimeout);
    } else {
      console.error("Maximale Anzahl an Neuverbindungsversuchen erreicht");
    }
  }

  handleAuth(token) {
    this.cleanup();

    if (token) {
      this.lastToken = token;
      setTimeout(() => {
        if (!this.connecting) {
          this.connect(token);
        }
      }, 500);
    } else {
      this.lastToken = null;
    }
  }

  startPing() {
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: "ping" });
      }
    }, 25000);
  }

  cleanup({ keepToken = false } = {}) {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    if (this.ws) {
      // Also closes sockets still connecting, otherwise they open later and leak.
      this.ws.close();
      this.ws = null;
    }
    this.connecting = false;
    if (!keepToken) {
      this.lastToken = null;
    }
    for (const [, interval] of this.heartbeatIntervals.entries()) {
      clearInterval(interval);
    }
    this.heartbeatIntervals.clear();
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const messageString = JSON.stringify(message);
      this.ws.send(messageString);
    }
  }

  lockRecord(prescriptionId) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.send({
      type: "lockRecord",
      prescriptionId: prescriptionId,
    });

    this.startHeartbeat(prescriptionId);
  }

  unlockRecord(prescriptionId) {
    this.send({
      type: "unlockRecord",
      prescriptionId,
    });

    this.stopHeartbeat(prescriptionId);
  }

  startHeartbeat(prescriptionId) {
    this.stopHeartbeat(prescriptionId);

    const interval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({
          type: "heartbeat",
          prescriptionId,
        });
      }
    }, 10000); // Alle 10 Sekunden

    this.heartbeatIntervals.set(prescriptionId, interval);
  }

  stopHeartbeat(prescriptionId) {
    const interval = this.heartbeatIntervals.get(prescriptionId);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(prescriptionId);
    }
  }
}

const wsClient = new WebSocketClient();

window.addEventListener("storage", (event) => {
  if (event.key === "token") {
    wsClient.handleAuth(event.newValue);
  }
});

export default wsClient;
